import React, { useState, useEffect } from 'react';
import { Music, BookOpen, Heart, Share2, Printer, ZoomIn, ZoomOut, Monitor, X } from 'lucide-react';
import Button from '../common/Button';

const FihiranaViewer = ({ fihirana, onClose }) => {
  const [fontSize, setFontSize] = useState(16);
  const [isFavorite, setIsFavorite] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);

  // Gérer la touche Échap pour quitter le mode présentation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && presentationMode) {
        setPresentationMode(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [presentationMode]);

  const increaseFontSize = () => {
    if (fontSize < 24) setFontSize(fontSize + 2);
  };

  const decreaseFontSize = () => {
    if (fontSize > 12) setFontSize(fontSize - 2);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    // Implémenter le partage
    if (navigator.share) {
      navigator.share({
        title: `Fihirana n°${fihirana.number} - ${fihirana.title}`,
        text: fihirana.lyrics
      });
    } else {
      alert('Partage non disponible sur ce navigateur');
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const togglePresentationMode = () => {
    setPresentationMode(!presentationMode);
  };

  // Formatter les paroles pour afficher les refrains et strophes
  const formatLyrics = (lyrics) => {
    if (!lyrics) return '';
    
    const lines = lyrics.split('\n');
    let formattedLines = [];
    let inRefrain = false;

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Détecter le début d'un refrain
      if (trimmedLine.toLowerCase().includes('refrain') || trimmedLine === 'Refrain:') {
        inRefrain = true;
        formattedLines.push(
          <div key={index} className="mt-4 mb-2 font-bold text-blue-600 dark:text-blue-400">
            {trimmedLine}
          </div>
        );
      }
      // Ligne vide - fin potentielle du refrain
      else if (trimmedLine === '') {
        inRefrain = false;
        formattedLines.push(<div key={index} className="h-4"></div>);
      }
      // Ligne normale
      else if (trimmedLine) {
        formattedLines.push(
          <div 
            key={index} 
            className={`${inRefrain ? 'text-blue-700 dark:text-blue-300 italic' : 'text-gray-800 dark:text-gray-200'} leading-relaxed`}
          >
            {trimmedLine}
          </div>
        );
      }
    });

    return formattedLines;
  };

  return (
    <div className={`space-y-6 ${presentationMode ? 'fixed inset-0 z-50 bg-black p-8 overflow-auto' : ''}`}>
      {/* Bouton fermer fixe en mode présentation */}
      {presentationMode && (
        <button
          onClick={togglePresentationMode}
          className="fixed top-4 right-4 z-50 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 shadow-lg transition-all transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          title="Quitter le mode présentation (Échap)"
        >
          <X className="w-6 h-6" />
        </button>
      )}

      {/* En-tête avec informations */}
      <div className={`${presentationMode ? 'bg-gradient-to-r from-gray-800 to-gray-900' : 'bg-gradient-to-r from-blue-600 to-purple-600'} text-white rounded-lg p-6`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-14 h-14 ${presentationMode ? 'bg-gray-700' : 'bg-white text-blue-600'} rounded-lg flex items-center justify-center font-bold text-xl`}>
                {fihirana.number}
              </div>
              <div>
                <h2 className={`${presentationMode ? 'text-4xl' : 'text-2xl'} font-bold`}>{fihirana.title}</h2>
                <p className={`${presentationMode ? 'text-gray-400 text-lg' : 'text-blue-100'} flex items-center gap-2 mt-1`}>
                  <BookOpen className="w-4 h-4" />
                  {fihirana.category}
                </p>
              </div>
            </div>
          </div>
          {!presentationMode && (
            <button
              onClick={toggleFavorite}
              className={`${
                isFavorite
                  ? 'text-red-300 fill-current'
                  : 'text-white'
              } hover:text-red-300 transition-colors`}
            >
              <Heart className="w-8 h-8" />
            </button>
          )}
        </div>

        {/* Statistiques */}
        {!presentationMode && (
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-blue-400">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4" />
              <span className="text-sm">{fihirana.favorites || 0} favoris</span>
            </div>
            {fihirana.lastUsed && (
              <div className="flex items-center gap-2">
                <Music className="w-4 h-4" />
                <span className="text-sm">
                  Dernière utilisation : {new Date(fihirana.lastUsed).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Barre d'outils */}
      {!presentationMode && (
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300 mr-2">Taille du texte :</span>
            <button
              onClick={decreaseFontSize}
              disabled={fontSize <= 12}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              title="Diminuer la taille"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium px-3 dark:text-gray-200">{fontSize}px</span>
            <button
              onClick={increaseFontSize}
              disabled={fontSize >= 24}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              title="Augmenter la taille"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={togglePresentationMode}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              title="Mode présentation"
            >
              <Monitor className="w-5 h-5" />
            </button>
            <button
              onClick={handleShare}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              title="Partager"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={handlePrint}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              title="Imprimer"
            >
              <Printer className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Paroles */}
      <div className={`${presentationMode ? 'bg-black text-white' : 'bg-white dark:bg-gray-800'} rounded-lg border border-gray-200 dark:border-gray-700 p-8 print:border-0`}>
        <div 
          className="font-serif"
          style={{ fontSize: presentationMode ? '32px' : `${fontSize}px` }}
        >
          {formatLyrics(fihirana.lyrics)}
        </div>
      </div>

      {/* Bouton quitter mode présentation */}
      {presentationMode && (
        <div className="flex justify-center">
          <Button
            onClick={togglePresentationMode}
            className="bg-gray-700 hover:bg-gray-600"
          >
            Quitter le mode présentation
          </Button>
        </div>
      )}

      {/* Historique d'utilisation (si disponible) */}
      {!presentationMode && fihirana.usageHistory && fihirana.usageHistory.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Historique d'utilisation</h3>
          <div className="space-y-2">
            {fihirana.usageHistory.map((usage, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-300">{usage.programme}</span>
                <span className="text-gray-500 dark:text-gray-400">{new Date(usage.date).toLocaleDateString('fr-FR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      {!presentationMode && (
        <div className="flex justify-end gap-3 pt-4 border-t print:hidden">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Fermer
          </Button>
          <Button
            onClick={handlePrint}
            icon={Printer}
          >
            Imprimer
          </Button>
        </div>
      )}

      {/* Styles pour l'impression */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:border-0,
          .print\\:border-0 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:border-0 {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default FihiranaViewer;
